import React, { createRef, forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import examples from 'libphonenumber-js/examples.mobile.json';
import parsePhoneNumber, { getExampleNumber, getCountryCallingCode } from 'libphonenumber-js';
import { Icon } from '../Icon';
import { Dropdown } from '../Dropdown';
import { InputContainer, InputElement, InputWrapper } from './Input';
const PhoneContainer = styled(InputContainer) `
  display: flex;
`;
const Label = styled.div `
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: 14px;
  font-weight: var(--font-regular, 500);
  color: ${({ theme }) => theme.secondary.background};
`;
const Up = styled(Icon.ChevronUpLarge) `
  --icon-size: 14px;
  --icon-color: currentColor;

  pointer-events: none;
`;
const Down = styled(Icon.ChevronDownLarge) `
  --icon-size: 14px;
  --icon-color: currentColor;

  pointer-events: none;
`;
const StyledDropdown = styled(Dropdown) `
  --dropdown-custom-padding: 0;
`;
const Dial = styled.div `
  white-space: nowrap;
  line-height: 16px;
`;
export const PhoneInput = forwardRef((props, ref) => {
    const [isHoveredState, setIsHoveredState] = useState(props.isHovered ?? false);
    const [isFocussedState, setIsFocussedState] = useState(props.isFocussed ?? false);
    const [isFilledState, setIsFilledState] = useState(props.isFilled ?? false);
    const onFocus = props.onFocus ??
        (() => {
            /* NOOP */
        });
    const onBlur = props.onBlur ??
        (() => {
            /* NOOP */
        });
    const onKeyDown = props.onKeyDown ??
        (() => {
            /* NOOP */
        });
    const [country, setCountry] = useState({ code: 'US', dial: '+1' });
    const [countryOpen, setCountryOpen] = useState(false);
    const inputRef = createRef();
    const styleProps = {
        isHovered: props.isHovered ?? isHoveredState,
        isFocussed: props.isFocussed ?? isFocussedState,
        isFilled: props.isFilled ?? isFilledState,
        isDisabled: props.isDisabled ?? false,
        isPressed: props.isPressed ?? false,
        hasError: props.hasError ?? false,
        size: props.size ?? 'default',
        variant: props.variant ?? 'default',
    };
    const handleClick = () => {
        if (props.isDisabled) {
            return;
        }
        if (props.onClick) {
            props.onClick();
        }
        setIsFocussedState(true);
    };
    const handleHoverChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onHoverChange) {
            props.onHoverChange(value);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onFocusChange) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && props.autoBlur) {
            inputRef.current?.blur();
        }
    };
    const handleValueChange = (value) => {
        if (props.onChange) {
            props.onChange(country.dial + value);
        }
    };
    useEffect(() => {
        if (props.isFocussed ?? isFocussedState) {
            inputRef.current?.focus();
        }
        else {
            inputRef.current?.blur();
        }
    }, [isFocussedState, props.isFocussed]);
    useEffect(() => {
        if (!props.value) {
            return;
        }
        setIsFilledState(props.value.length > 0);
        const phoneNumber = parsePhoneNumber(props.value);
        if (phoneNumber && phoneNumber.country) {
            setCountry({
                code: phoneNumber.country,
                dial: `+${phoneNumber.countryCallingCode}`,
            });
        }
    }, [props.value]);
    return (React.createElement(InputWrapper, { ref: ref, style: props.style, className: props.className, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onClick: handleClick, ...styleProps },
        React.createElement(PhoneContainer, { ...styleProps },
            React.createElement(StyledDropdown, { options: props.countryList.map((c) => ({
                    label: `${c.name} (+${c.dial.replace('+', '')})`,
                    value: c.code,
                })), active: country.code, onChange: (value) => {
                    setCountry({
                        code: value ? value.toString() : '',
                        dial: `+${getCountryCallingCode(value?.toString())}`,
                    });
                }, onOpenChange: setCountryOpen, customLabel: React.createElement(Label, null,
                    country.code,
                    countryOpen ? React.createElement(Up, null) : React.createElement(Down, null)), offsetX: styleProps.size === 'small' ? -12 : -16, offsetY: styleProps.size === 'small' ? 4 : 12, search: true }),
            React.createElement(Dial, null, country.dial),
            React.createElement(InputElement, { onChange: (e) => handleValueChange(e.target.value), onFocus: (e) => {
                    handleFocusChange(true);
                    onFocus(e);
                }, onBlur: (e) => {
                    handleFocusChange(false);
                    onBlur(e);
                }, onKeyDown: onKeyDown, onKeyPress: handleKeyPress, autoFocus: props.autoFocus, ref: inputRef, disabled: props.isDisabled ?? false, value: props.value ? props.value.replace(country.dial, '') : '', type: "tel", pattern: "^[0-9]*$", readOnly: props.readOnly ?? false, maxLength: props.maxLength, placeholder: props.placeholder ?? getExampleNumber(country.code, examples)?.nationalNumber })),
        props.children));
});
//# sourceMappingURL=Phone.js.map